import { Adapter } from "@/core/models/adapter";
import { CategoryAdapter } from "@/core/models/category";
import sanitizeUtils from "@/core/utils/sanitize.utils";
import { CircuitInstructionAdapter } from "@/core/models/circuitInstruction";
import { CircuitAdapter } from "@/core/models/circuit";
import { cloneDeep } from "@/core/utils/utils";

export const DISPATCH_CIRCUIT_RULES_ENABLED = "enabled";
export const DISPATCH_CIRCUIT_RULES_DISABLED = "disabled";

export const DISPATCH_CIRCUIT_RULES_STATUS = [DISPATCH_CIRCUIT_RULES_ENABLED, DISPATCH_CIRCUIT_RULES_DISABLED];

export class DispatchCircuitRule {
  constructor() {
    this.id = null;
    this.circuit_id = null;
    this.circuit_name = null;
    this.circuit = null;
    this.allowedCategoryIds = [];
    this.description = null;
    this.circuitInstructions = [];
    this.instructions = [];
    this.terms = null;
    this.traceabilityEnabled = null;
    this.categoryIds = [];
    this.categoryLabels = null;
    this.status = null;
    this.highlightedInstruction = null;
    this.eligibleCountries = [];
    /** @var {array} */
    this.customValues = [];
  }

  /**
   * @return {string}
   */
  getJsonLdId() {
    return `/dispatch-circuit-rules/${this.id}`;
  }

  /**
   * @return {string}
   */
  getCircuitJsonLdId() {
    return `/circuits/${this.circuit_id}`;
  }

  /**
   * @returns {null | string}
   */
  getCircuitId() {
    return this.circuit?.id;
  }

  /**
   * @return {boolean}
   */
  circuitNeedsWarehouse() {
    return this.circuit.needsWarehouse();
  }

  /**
   * @return {boolean}
   */
  circuitIsStore() {
    return this.circuit.isStore();
  }

  /**
   * @returns {null | string}
   */
  getCircuitDeposit() {
    return this.circuit?.deposit;
  }

  /**
   * @returns {null | string}
   */
  getCircuitChannel() {
    return this.circuit?.channel;
  }

  getCircuitName() {
    return this.circuit_name || this.circuit?.name;
  }

  /**
   * @returns {CircuitInstruction[]}
   */
  getDefaultInstructions() {
    return cloneDeep(this.circuit.defaultInstructions);
  }

  getEligibleCountries() {
    return this.eligibleCountries;
  }
}

export class DispatchCircuitRuleAdapter extends Adapter {
  static adapt(dispatchCircuitRuleApi) {
    const circuit = CircuitAdapter.adapt(dispatchCircuitRuleApi?.circuit);

    const dispatchCircuitRule = new DispatchCircuitRule();
    dispatchCircuitRule.id = dispatchCircuitRuleApi?.id;
    dispatchCircuitRule.circuit = circuit;
    dispatchCircuitRule.circuit_id = circuit?.id;
    dispatchCircuitRule.circuit_name = dispatchCircuitRuleApi?.circuit_name;
    dispatchCircuitRule.allowedCategoryIds = dispatchCircuitRuleApi?.categoryIds ?? [];
    dispatchCircuitRule.description =
      sanitizeUtils.sanitize(dispatchCircuitRuleApi?.description) ??
      sanitizeUtils.sanitize(dispatchCircuitRuleApi?.circuit?.defaultDescription);
    dispatchCircuitRule.terms = dispatchCircuitRuleApi?.terms;
    dispatchCircuitRule.traceabilityEnabled = dispatchCircuitRuleApi?.traceabilityEnabled;
    dispatchCircuitRule.categoryIds = dispatchCircuitRuleApi?.categoryIds;
    dispatchCircuitRule.categoryCount = dispatchCircuitRuleApi?.categoryCount ?? null;
    dispatchCircuitRule.categoryLabels = dispatchCircuitRuleApi?.categoryLabels ?? null;
    dispatchCircuitRule.categories = dispatchCircuitRuleApi?.categories
      ? Object.values(dispatchCircuitRuleApi?.categories).map((c) => CategoryAdapter.adapt(c))
      : null;
    dispatchCircuitRule.status = dispatchCircuitRuleApi?.status;
    dispatchCircuitRule.highlightedInstruction = dispatchCircuitRuleApi?.highlightedInstruction;
    dispatchCircuitRule.eligibleCountries = dispatchCircuitRuleApi?.eligibleCountries;

    dispatchCircuitRule.customValues = dispatchCircuitRuleApi?.customValues;

    if (dispatchCircuitRuleApi?.instructions) {
      dispatchCircuitRule.circuitInstructions = dispatchCircuitRuleApi.instructions.map((instructionApi) => {
        const circuitInstruction = CircuitInstructionAdapter.adapt(instructionApi);
        circuitInstruction.dispatchCircuitRuleId = dispatchCircuitRule.id;
        return circuitInstruction;
      });
    }

    if (dispatchCircuitRule.circuitInstructions.length) {
      dispatchCircuitRule.instructions = dispatchCircuitRule.circuitInstructions;
    } else if (dispatchCircuitRule.getDefaultInstructions()?.length) {
      dispatchCircuitRule.instructions = cloneDeep(
        dispatchCircuitRule.getDefaultInstructions().map((instruction) => {
          let dci = cloneDeep(CircuitInstructionAdapter.adapt(instruction));
          // Puisque ce sont les instructions par défaut (liés au cicuit)
          // On leur enlève cette liaison pour en créer de nouvelles liées au dispatchCircuitRule
          dci.circuitId = null;
          dci.circuit = null;
          dci.id = null;
          return dci;
        })
      );
    }

    // Tri par séquence
    if (dispatchCircuitRule?.instructions?.length) {
      dispatchCircuitRule.instructions.sort((a, b) => a.sequence - b.sequence);
    }

    return dispatchCircuitRule;
  }
}
